import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AudienceConflictDto } from '@tinct/core-api-models';
import { useValidateCampaignAudience } from '@tinct/core-api';
import {
  useCurrentWorkspaceUrlPrefix,
  useGetCurrentWorkspaceId,
} from '@tinct/core-workspace';

/**
 * The shared "Preview & Refine" entry point: validate the campaign audience,
 * surface conflicts in the AudienceConflictModal, then deep-link into the
 * funnel's preview step. One implementation for the details header and the
 * settings tab (it used to be copy-pasted in both).
 */
export function usePreviewAndRefine(campaignId: string) {
  const navigate = useNavigate();
  const workspaceUrl = useCurrentWorkspaceUrlPrefix();
  const workspaceId = useGetCurrentWorkspaceId();

  const [conflicts, setConflicts] = useState<AudienceConflictDto[]>([]);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const { mutateAsync: validateAudience, isPending: isValidating } =
    useValidateCampaignAudience();

  // Management surfaces edit in place — the studio route, not the creation
  // funnel's preview step (same canvas, campaign-framed chrome). Landing on
  // the variants tab: "Preview & Refine" is about reviewing the personalized
  // versions, not editing the original page.
  const goToPreview = () =>
    navigate(`${workspaceUrl}/campaigns/${campaignId}/studio?view=variants`);

  const handlePreviewAndRefine = async () => {
    try {
      const result = await validateAudience({ id: campaignId, organizationId: workspaceId });
      if (result.conflicts && result.conflicts.length > 0) {
        setConflicts(result.conflicts);
        setShowConflictModal(true);
        return;
      }
      goToPreview();
    } catch (e) {
      console.error('Error validating audience:', e);
    }
  };

  const handleExcludeAndContinue = async () => {
    try {
      await validateAudience({
        id: campaignId,
        organizationId: workspaceId,
        excludeConflicts: true,
      });
      setShowConflictModal(false);
      setConflicts([]);
      goToPreview();
    } catch (e) {
      console.error('Error excluding conflicts and continuing:', e);
    }
  };

  return {
    conflicts,
    showConflictModal,
    setShowConflictModal,
    isValidating,
    handlePreviewAndRefine,
    handleExcludeAndContinue,
  };
}
